'use client';

import InlineStyle from '@/components/InlineStyle';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { Activity, Clock, MessageSquareWarning, Radar, ShieldCheck, TrendingUp } from 'lucide-react';
import {
  Button,
  Card,
  CheckList,
  FaqAccordion,
  PageHero,
  PageSection,
  SectionTitle,
} from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import BookDemoSection from '@/components/BookDemoSection';
import { fadeUp, staggerContainer, viewportOnce } from '@/lib/motion';
import { HERO_SIZES } from '@/lib/responsiveImage';

const METRICS = [
  { value: '7 days', label: 'From kickoff to readout' },
  { value: '20+', label: 'Channels benchmarked' },
  { value: '90-day', label: 'Action roadmap included' },
];

const PROBLEMS = [
  'You can’t see how fast your team actually responds versus your category.',
  'Negative sentiment spikes are noticed late — after they’ve spread.',
  'Conversations are scattered across channels with no single baseline.',
  'You’re about to invest in tooling or headcount without an objective starting point.',
];

const CAPABILITIES = [
  { Icon: Radar, title: 'Channel coverage map', body: 'Where your customers are talking — and where you’re absent — across 20+ social and review channels.' },
  { Icon: Clock, title: 'Response-time benchmark', body: 'Your first-response and resolution times measured against your category leaders.' },
  { Icon: Activity, title: 'Sentiment baseline', body: 'A clear read on positive, negative, and neutral conversation share, with the drivers behind each.' },
  { Icon: MessageSquareWarning, title: 'Gap analysis', body: 'The specific failure modes — missed mentions, slow escalations, blind spots — costing you goodwill.' },
  { Icon: TrendingUp, title: 'Opportunity sizing', body: 'The measurable upside of closing each gap, ranked by impact and effort.' },
  { Icon: ShieldCheck, title: '90-day roadmap', body: 'A prioritised action plan your team can execute immediately — with or without Locobuzz.' },
];

export default function AiCxAuditBody({ faqs = [] }) {
  return (
    <div>
      <span className="sr-only">
        Locobuzz offers a free, AI-powered 7-day Customer Experience Audit that benchmarks an
        enterprise brand’s social and digital CX — sentiment, first-response time, channel coverage,
        and resolution gaps — against its category, and delivers a prioritised 90-day action roadmap.
      </span>

      {/* 1 — Hero */}
      <PageHero
        eyebrow="Free CX Audit"
        title={
          <>
            See exactly where your customer experience is{' '}
            <span className="serif-accent italic">leaking</span>
          </>
        }
        subtitle={
          <>
            Get an AI-powered, 7-day benchmark of your brand’s social CX — sentiment, response gaps,
            and channel coverage — with a 90-day roadmap to fix what matters most.
          </>
        }
        actions={
          <Button variant="cta" size="lg" href="#book-demo">
            Book my free audit
          </Button>
        }
        visual={
          <div className="lp-hero-visual">
            <Image
              src="/locobuzz-hero-image-1.webp"
              alt="Locobuzz AI customer experience audit dashboard showing sentiment, response times, and channel coverage"
              width={960}
              height={960}
              sizes={HERO_SIZES}
              priority
              fetchPriority="high"
            />
          </div>
        }
      />

      {/* 2 — Proof metrics */}
      <MetricsBand items={METRICS} columns={3} />

      {/* 3 — Problem / solution */}
      <PageSection bg="tint">
        <div className="lp-split">
          <SectionTitle
            align="left"
            eyebrow="Why audit first"
            title={
              <>
                You can’t fix what you can’t{' '}
                <span className="serif-accent italic">measure</span>
              </>
            }
            subtitle="Most brands invest in CX tools and teams without an objective baseline. The audit gives you one — in a week."
          />
          <Card padding="xl" radius="2xl">
            <CheckList items={PROBLEMS} />
          </Card>
        </div>
      </PageSection>

      {/* 4 — What you get (capability cards) */}
      <PageSection bg="page">
        <SectionTitle
          align="center"
          eyebrow="Inside the audit"
          title={
            <>
              Six benchmarks, one clear{' '}
              <span className="serif-accent italic">scorecard</span>
            </>
          }
          subtitle="Every audit covers the same six dimensions so your results are comparable and actionable."
        />
        <motion.div
          className="lp-grid"
          variants={staggerContainer(0.08)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          {CAPABILITIES.map(({ Icon, title, body }) => (
            <motion.div key={title} variants={fadeUp}>
              <Card padding="xl" radius="2xl" hover className="lp-card">
                <span className="lp-icon" aria-hidden="true">
                  <Icon size={22} color="var(--brand)" strokeWidth={2.2} />
                </span>
                <h3 className="lp-card-title">{title}</h3>
                <p className="lp-card-body">{body}</p>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </PageSection>

      {/* 5 — FAQ */}
      <PageSection id="faqs" bg="tint">
        <SectionTitle
          align="center"
          title={
            <>
              Everything you need to{' '}
              <span className="serif-accent italic">know</span>
            </>
          }
        />
        <FaqAccordion items={faqs} columns={2} name="ai-cx-audit-faq" />
      </PageSection>

      {/* 6 — Book demo (always last) */}
      <BookDemoSection />

      <InlineStyle css={`
        .lp-hero-visual {
          border-radius: var(--radius-2xl);
          overflow: hidden;
          box-shadow: var(--shadow-panel);
          border: 1px solid var(--brand-border);
          background: var(--surface-card, #fff);
        }
        .lp-hero-visual img {
          display: block;
          width: 100%;
          height: auto;
        }
        .lp-split {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 48px;
          align-items: center;
        }
        .lp-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }
        .lp-card { height: 100%; }
        .lp-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          border-radius: var(--radius-lg);
          background: var(--brand-subtle);
          border: 1px solid var(--brand-border);
          margin-bottom: 16px;
        }
        .lp-card-title {
          font-size: 19px;
          font-weight: 700;
          color: var(--text-heading, #0D1B3E);
          margin: 0 0 8px;
          letter-spacing: -0.01em;
        }
        .lp-card-body {
          font-size: 14.5px;
          line-height: 1.65;
          color: var(--text-body);
          margin: 0;
        }
        @media (max-width: 900px) {
          .lp-split { grid-template-columns: 1fr; gap: 32px; }
          .lp-grid { grid-template-columns: 1fr; }
        }
      `} />
    </div>
  );
}
